import { format } from "date-fns";
import { safeParseDate } from "@/lib/utils/date";

export type NotificationBadgeTone = "info" | "success" | "danger" | "neutral";

export function getNotificationTypeTone(
  type: string | null | undefined,
): NotificationBadgeTone {
  const normalized = (type ?? "").toLowerCase();

  if (normalized.includes("payment")) return "success";
  if (normalized.includes("enroll")) return "info";
  if (normalized.includes("class") || normalized.includes("attendance")) {
    return "danger";
  }

  return "neutral";
}

export function formatNotificationTypeLabel(
  type: string | null | undefined,
): string {
  if (!type) return "Unknown";

  const withoutEventSuffix = type.replace(/Event$/, "");
  return withoutEventSuffix
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .trim();
}

export const NOTIFICATION_FILTER_ALL = "all";
export const NOTIFICATION_FILTER_UNREAD = "unread";
export const NOTIFICATION_FILTER_READ = "read";
export const NOTIFICATION_TYPE_UNKNOWN = "__unknown__";

const NOTIFICATION_TYPE_FILTER_PREFIX = "type:";

export function getNotificationFilterOptions(
  notifications: { type?: string | null }[],
): { label: string; value: string }[] {
  const options = [
    { label: "All", value: NOTIFICATION_FILTER_ALL },
    { label: "Unread", value: NOTIFICATION_FILTER_UNREAD },
    { label: "Read", value: NOTIFICATION_FILTER_READ },
  ];

  const types = new Map<string, string>();

  for (const notification of notifications) {
    const raw = notification.type ?? "";
    const key = raw || NOTIFICATION_TYPE_UNKNOWN;
    if (!types.has(key)) {
      types.set(key, formatNotificationTypeLabel(notification.type));
    }
  }

  for (const [value, label] of [...types.entries()].sort((a, b) =>
    a[1].localeCompare(b[1]),
  )) {
    options.push({
      label,
      value: `${NOTIFICATION_TYPE_FILTER_PREFIX}${value}`,
    });
  }

  return options;
}

export function matchesNotificationFilter(
  notification: { type?: string | null; isRead: boolean },
  filterValue: string,
): boolean {
  if (filterValue === NOTIFICATION_FILTER_ALL) return true;
  if (filterValue === NOTIFICATION_FILTER_UNREAD) return !notification.isRead;
  if (filterValue === NOTIFICATION_FILTER_READ) return notification.isRead;

  if (filterValue.startsWith(NOTIFICATION_TYPE_FILTER_PREFIX)) {
    const typeKey = filterValue.slice(NOTIFICATION_TYPE_FILTER_PREFIX.length);
    if (typeKey === NOTIFICATION_TYPE_UNKNOWN) return !notification.type;
    return (notification.type ?? "") === typeKey;
  }

  return true;
}

/** Table/list display for notification `createdAt` (ISO string or Date). */
export function formatNotificationCreatedAt(
  value: Date | string | null | undefined,
): string {
  const date = safeParseDate(value);
  if (!date) return "Unknown";

  return format(date, "MMM d, yyyy · h:mm a");
}
